import React, { Suspense } from 'react';

export enum IconName {
  ADD_TAG = 'add-tag',
  ALERT_CIRCLE = 'alert-circle',
  ALERT_TRIANGLE = 'alert-triangle',
  APARTMENTS_COM = 'apartments-com',
  ARROW_RIGHT = 'arrow-right',
  ATTACHMENT = 'attachment',
  ATTACHMENT_GREY = 'attachment-grey',
  BACK = 'back',
  BATH = 'bath',
  BELL = 'bell',
  BULLHORN = 'bullhorn',
  BANK_CARD = 'bank-card',
  BANK_CARD_FILLED = 'bank-card-filled',
  PLAID_BANK = 'plaid-bank',
  PLAID_CARD = 'plaid-card',
  BLUE_PLUS = 'blue-plus',
  BOOK = 'book',
  BUILDING = 'building',
  CALCULATOR = 'calculator',
  CALENDAR = 'calendar',
  CALENDAR_TIME = 'calendar-time',
  CALLING = 'calling',
  CARD_WALLET = 'card-wallet',
  CHECK_CIRCLE = 'check-circle',
  CHEVRON_DOWN = 'chevron-down',
  CLIPBOARD_TEXT = 'clipboard-text',
  CLOSE_REMOVE = 'close-remove',
  COPY = 'copy',
  DASHBOARD = 'dashboard',
  COMPOSER = 'composer',
  COLOR_BUCKET = 'color-bucket',
  CRAIGSLIST_TEXT = 'craigslist-text',
  COUNTER_CLOCKWISE_UNDO = 'counter-clockwise-undo',
  DELETE = 'delete',
  DOCS = 'docs',
  DOUBLE_BED = 'double-bed',
  DOUBLE_CHECK = 'double-check',
  DOCUMENT_CHECK = 'document-check',
  DOWNLOAD = 'download',
  DOWN_TWICE = 'down-twice',
  EDIT = 'edit',
  EDIT_BUDGETS = 'edit-budgets',
  EMAIL = 'email',
  EXTERNAL_LINK = 'external-link',
  FILTER = 'filter',
  FLOPPY_DISK_SAVE_3 = 'floppy-disk-save-3',
  FORMULA = 'formula',
  FX_FORMULA = 'fx-formula',
  HOME_FILLED = 'home-filled',
  HOTPADS = 'hotpads',
  INBOX = 'inbox',
  INBOX_DOWN = 'inbox-down',
  INFO = 'info',
  INFO_CIRCLE = 'info-circle',
  KING_BED = 'king-bed',
  LARGE_CHECK_CIRCLE = 'large-check-circle',
  LAYERS = 'layers',
  LOCATION = 'location',
  LOADING = 'loading',
  LOCK = 'lock',
  LOGOUT = 'logout',
  NAV_EXPAND = 'nav-expand',
  MESSAGES = 'messages',
  PORTAL_CHAT = 'portal-chat',
  SQUARE_MESSAGE = 'square-message',
  NOTEBOOK = 'notebook',
  PAPER_PLUS = 'paper-plus',
  PEN = 'pen',
  PEN_SIGNATURE = 'pen-signature',
  PERSON = 'person',
  PETS = 'pets',
  PHONE = 'phone',
  PLUS_4 = 'plus-4',
  PRINTER_2 = 'printer-2',
  PRINTER_OUTLINE = 'printer-outline',
  PROPERTY_MATRIX = 'property-matrix',
  PUSH_TO_TODAY = 'push-to-today',
  QUESTION_MARK = 'question-mark',
  QUESTION_MARK_CIRCLE = 'question-mark-circle',
  REDFIN = 'redfin',
  REALTOR_COM = 'realtor-com',
  REPLAY_1 = 'replay-1',
  RECEIPT_BILL_ADD = 'receipt-bill-add',
  RECEIPT_BILL_SUCCESS = 'receipt-bill-success',
  REPLY_SHARE_LEFT = 'reply-share-left',
  SCANS = 'scans',
  SAVINGS_PIG = 'savings-pig',
  SEEN_CHECKMARK = 'seen-checkmark',
  SEND = 'send',
  SEND_MENU = 'send-menu',
  SETTING = 'setting',
  SHIELD_CHECK = 'shield-check',
  SHOW = 'show',
  SPARKLE = 'sparkle',
  SORT_DOWN = 'sort-down',
  SORT_UP = 'sort-up',
  STARS = 'stars',
  STORE = 'store',
  SHOWER = 'shower',
  SWAP_HORIZONTAL = 'swap-horizontal',
  SWAP_VERTICAL = 'swap-vertical',
  SWITCH_BUTTON_OFF = 'switch-button-off',
  TIME_CIRCLE = 'time-circle',
  TRULIA = 'trulia',
  TWO_USERS = 'two-users',
  TWO_USERS_FILLED = 'two-users-filled',
  UNIVERSITY = 'university',
  UPLOAD = 'upload',
  VEHICLE = 'vehicle',
  WHITE_PLUS = 'white-plus',
  WARNING_TRIANGLE = 'warning-triangle',
  WRENCH = 'wrench',
  WORK = 'work',
  WORKSPACE_SAMPLE_DATA = 'workspace-sample-data',
  WORKSPACE_START_BLANK = 'workspace-start-blank',
  ZILLOW = 'zillow',
  DOCUMENT = 'document',
  WALLET = 'wallet',
  DOLLAR_BADGE = 'dollar-badge',
  DOLLAR_SEND = 'dollar-send',
  FILLED_EMAIL = 'filled-email',
  GRAPH = 'graph',
  GRADIENT_SPINNER = 'gradient-spinner',
  CREATION_OUTLINE = 'creation-outline',
  IMAGE_PLUS = 'image-plus',
  LARGE_EMAIL = 'large-email',
  ASSISTANT_ROBOT = 'assistant-robot',
  ASSISTANT_WOOF = 'assistant-woof',
  ASSISTANT_HOMEY = 'assistant-homey',
  ASSISTANT_SAGE = 'assistant-sage',
  ASSISTANT_SPARK = 'assistant-spark',
  ASSISTANT_NOVA = 'assistant-nova',
  ASSISTANT_CHECK_CIRCLE = 'assistant-check-circle',
}

type IconModule = { default: React.ComponentType };
type IconImporter = () => Promise<IconModule>;

const ICON_IMPORTERS = import.meta.glob('./app_icons/*.tsx') as Record<
  string,
  IconImporter
>;

const lazyIconCache: Record<
  string,
  React.LazyExoticComponent<React.ComponentType> | undefined
> = {};

const iconNameToPascalCase = (iconName: string) => {
  return iconName
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join('');
};

const getIconImporter = (iconName: string): IconImporter | undefined => {
  const pascalCaseFileName = iconNameToPascalCase(iconName);
  return ICON_IMPORTERS[`./app_icons/${pascalCaseFileName}.tsx`];
};

const getLazyIcon = (
  iconName: string
): React.LazyExoticComponent<React.ComponentType> | null => {
  const cached = lazyIconCache[iconName];
  if (cached) return cached;

  const importer = getIconImporter(iconName);
  if (!importer) return null;

  const LazyIcon = React.lazy(importer);
  lazyIconCache[iconName] = LazyIcon;
  return LazyIcon;
};

interface AppIconProps {
  iconName: string;
}

const AppIcon = ({ iconName }: AppIconProps) => {
  const LazyIcon = getLazyIcon(iconName);
  if (!LazyIcon) return null;

  return (
    <Suspense fallback={null}>
      <LazyIcon />
    </Suspense>
  );
};

export const getIconByName = (iconName: string) => {
  if (!getIconImporter(iconName)) return null;
  return <AppIcon iconName={iconName} />;
};

export const MaintenanceTroubleshootingChevronIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.71 7.70008L11.12 12.2901L15.71 16.8801L14.29 18.2901L8.29 12.2901L14.29 6.29008L15.71 7.70008Z"
      fill="currentColor"
    />
  </svg>
);

export const PaperPlusIcon = () => getIconByName(IconName.PAPER_PLUS);
export const BuildingIcon = () => getIconByName(IconName.BUILDING);
export const SavingsPigIcon = () => getIconByName(IconName.SAVINGS_PIG);
export const Replay1Icon = () => getIconByName(IconName.REPLAY_1);
export const SwapHorizontalIcon = () => getIconByName(IconName.SWAP_HORIZONTAL);
export const SwapVerticalIcon = () => getIconByName(IconName.SWAP_VERTICAL);
export const NotebookIcon = () => getIconByName(IconName.NOTEBOOK);
export const CalculatorIcon = () => getIconByName(IconName.CALCULATOR);
export const CalendarIcon = () => getIconByName(IconName.CALENDAR);
export const CalendarTimeIcon = () => getIconByName(IconName.CALENDAR_TIME);
export const BookIcon = () => getIconByName(IconName.BOOK);
export const DocsIcon = () => getIconByName(IconName.DOCS);
export const Plus4Icon = () => getIconByName(IconName.PLUS_4);
export const CloseRemoveIcon = () => getIconByName(IconName.CLOSE_REMOVE);
export const CopyIcon = () => getIconByName(IconName.COPY);
export const SwitchButtonOffIcon = () =>
  getIconByName(IconName.SWITCH_BUTTON_OFF);
export const LogoutIcon = () => getIconByName(IconName.LOGOUT);
export const SendIcon = () => getIconByName(IconName.SEND);
export const EditIcon = () => getIconByName(IconName.EDIT);
export const ReplyShareLeftIcon = () =>
  getIconByName(IconName.REPLY_SHARE_LEFT);
export const CardWalletIcon = () => getIconByName(IconName.CARD_WALLET);
export const CounterClockwiseUndoIcon = () =>
  getIconByName(IconName.COUNTER_CLOCKWISE_UNDO);
export const Printer2Icon = () => getIconByName(IconName.PRINTER_2);
export const PrinterOutlineIcon = () => getIconByName(IconName.PRINTER_OUTLINE);
export const InfoIcon = () => getIconByName(IconName.INFO);
export const InfoCircleIcon = () => getIconByName(IconName.INFO_CIRCLE);
export const ImagePlusIcon = () => getIconByName(IconName.IMAGE_PLUS);
export const InboxDownIcon = () => getIconByName(IconName.INBOX_DOWN);
export const FilterIcon = () => getIconByName(IconName.FILTER);
export const DownTwiceIcon = () => getIconByName(IconName.DOWN_TWICE);
export const MessagesIcon = () => getIconByName(IconName.MESSAGES);
export const PortalChatIcon = () => getIconByName(IconName.PORTAL_CHAT);
export const SquareMessageIcon = () => getIconByName(IconName.SQUARE_MESSAGE);
export const PhoneIcon = () => getIconByName(IconName.PHONE);
export const QuestionMarkIcon = () => getIconByName(IconName.QUESTION_MARK);
export const QuestionMarkCircleIcon = () =>
  getIconByName(IconName.QUESTION_MARK_CIRCLE);
export const DeleteIcon = () => getIconByName(IconName.DELETE);
export const DoubleCheckIcon = () => getIconByName(IconName.DOUBLE_CHECK);
export const SendMenuIcon = () => getIconByName(IconName.SEND_MENU);
export const SettingIcon = () => getIconByName(IconName.SETTING);
export const ShieldCheckIcon = () => getIconByName(IconName.SHIELD_CHECK);
export const AttachmentIcon = () => getIconByName(IconName.ATTACHMENT);
export const AddTagIcon = () => getIconByName(IconName.ADD_TAG);
export const PenIcon = () => getIconByName(IconName.PEN);
export const PenSignatureIcon = () => getIconByName(IconName.PEN_SIGNATURE);
export const FormulaIcon = () => getIconByName(IconName.FORMULA);
export const FloppyDiskSave3Icon = () =>
  getIconByName(IconName.FLOPPY_DISK_SAVE_3);
export const ColorBucketIcon = () => getIconByName(IconName.COLOR_BUCKET);
export const EditBudgetsIcon = () => getIconByName(IconName.EDIT_BUDGETS);
export const ArrowRightIcon = () => getIconByName(IconName.ARROW_RIGHT);
export const CallingIcon = () => getIconByName(IconName.CALLING);
export const EmailIcon = () => getIconByName(IconName.EMAIL);
export const LargeEmailIcon = () => getIconByName(IconName.LARGE_EMAIL);
export const ExternalLinkIcon = () => getIconByName(IconName.EXTERNAL_LINK);
export const LayersIcon = () => getIconByName(IconName.LAYERS);
export const LocationIcon = () => getIconByName(IconName.LOCATION);
export const LockIcon = () => getIconByName(IconName.LOCK);
export const PersonIcon = () => getIconByName(IconName.PERSON);
export const PetsIcon = () => getIconByName(IconName.PETS);
export const VehicleIcon = () => getIconByName(IconName.VEHICLE);
export const TimeCircleIcon = () => getIconByName(IconName.TIME_CIRCLE);
export const UploadIcon = () => getIconByName(IconName.UPLOAD);
export const WorkIcon = () => getIconByName(IconName.WORK);
export const WorkspaceSampleDataIcon = () =>
  getIconByName(IconName.WORKSPACE_SAMPLE_DATA);
export const WorkspaceStartBlankIcon = () =>
  getIconByName(IconName.WORKSPACE_START_BLANK);
export const DollarIcon = () => getIconByName('dollar');
export const HomeIcon = () => getIconByName('home');
export const DownloadIcon = () => getIconByName(IconName.DOWNLOAD);
export const CheckmarkIcon = () => getIconByName('checkmark');
export const LoadingIcon = () => getIconByName(IconName.LOADING);
export const TwoUsersIcon = () => getIconByName(IconName.TWO_USERS);
export const CloseCircleIcon = () => getIconByName('close-circle');
export const MinusCircleIcon = () => getIconByName('minus-circle');
export const DoubleBed = () => getIconByName(IconName.DOUBLE_BED);
export const Bath = () => getIconByName(IconName.BATH);
export const KingBed = () => getIconByName(IconName.KING_BED);
export const Shower = () => getIconByName(IconName.SHOWER);
export const ChevronDown = () => getIconByName(IconName.CHEVRON_DOWN);
export const Redfin = () => getIconByName(IconName.REDFIN);
export const RealtorCom = () => getIconByName(IconName.REALTOR_COM);
export const Zillow = () => getIconByName(IconName.ZILLOW);
export const PropertyMatrix = () => getIconByName(IconName.PROPERTY_MATRIX);
export const Trulia = () => getIconByName(IconName.TRULIA);
export const ApartmentsCom = () => getIconByName(IconName.APARTMENTS_COM);
export const HotPads = () => getIconByName(IconName.HOTPADS);
export const CraigslistText = () => getIconByName(IconName.CRAIGSLIST_TEXT);
export const AlertTriangleIcon = () => getIconByName(IconName.ALERT_TRIANGLE);
export const FxFormulaIcon = () => getIconByName(IconName.FX_FORMULA);
export const WhitePlusIcon = () => getIconByName(IconName.WHITE_PLUS);
export const DocumentIcon = () => getIconByName(IconName.DOCUMENT);
export const AttachmentGreyIcon = () => getIconByName(IconName.ATTACHMENT_GREY);
export const BackIcon = () => getIconByName(IconName.BACK);
export const BankCardIcon = () => getIconByName(IconName.BANK_CARD);
export const BankCardFilledIcon = () =>
  getIconByName(IconName.BANK_CARD_FILLED);
export const UniversityIcon = () => getIconByName(IconName.UNIVERSITY);
export const CheckCircleIcon = () => getIconByName(IconName.CHECK_CIRCLE);
export const LargeCheckCircleIcon = () =>
  getIconByName(IconName.LARGE_CHECK_CIRCLE);
export const AlertCircleIcon = () => getIconByName(IconName.ALERT_CIRCLE);
export const SeenCheckmarkIcon = () => getIconByName(IconName.SEEN_CHECKMARK);
export const BluePlusIcon = () => getIconByName(IconName.BLUE_PLUS);
export const WarningTriangleIcon = () =>
  getIconByName(IconName.WARNING_TRIANGLE);
export const WalletIcon = () => getIconByName(IconName.WALLET);
export const DollarBadgeIcon = () => getIconByName(IconName.DOLLAR_BADGE);
export const DollarSendIcon = () => getIconByName(IconName.DOLLAR_SEND);
export const FilledEmailIcon = () => getIconByName(IconName.FILLED_EMAIL);
export const SortUpIcon = () => getIconByName(IconName.SORT_UP);
export const SortDownIcon = () => getIconByName(IconName.SORT_DOWN);
export const StarsIcon = () => getIconByName(IconName.STARS);
export const PushToTodayIcon = () => getIconByName(IconName.PUSH_TO_TODAY);
export const GraphIcon = () => getIconByName(IconName.GRAPH);
export const GradientSpinnerIcon = () =>
  getIconByName(IconName.GRADIENT_SPINNER);
export const ComposerIcon = () => getIconByName(IconName.COMPOSER);
export const CreationOutlineIcon = () =>
  getIconByName(IconName.CREATION_OUTLINE);
export const ShowIcon = () => getIconByName(IconName.SHOW);
export const SparkleIcon = () => getIconByName(IconName.SPARKLE);
export const AssistantRobotIcon = () => getIconByName(IconName.ASSISTANT_ROBOT);
export const AssistantWoofIcon = () => getIconByName(IconName.ASSISTANT_WOOF);
export const AssistantHomeyIcon = () => getIconByName(IconName.ASSISTANT_HOMEY);
export const AssistantSageIcon = () => getIconByName(IconName.ASSISTANT_SAGE);
export const AssistantSparkIcon = () => getIconByName(IconName.ASSISTANT_SPARK);
export const AssistantNovaIcon = () => getIconByName(IconName.ASSISTANT_NOVA);
export const AssistantCheckCircleIcon = () =>
  getIconByName(IconName.ASSISTANT_CHECK_CIRCLE);
